import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildFilterString } from './buildFilterString';

import { DefaultButton, SpinnerSize, Spinner } from '@fluentui/react';
import { useHistory, useLocation } from 'react-router-dom';
import YouNeed from '../../youNeed';
import './SearchResults.scss';
import { useSearchResult, useDownloadSearchResults } from 'app/services/search-api';
import { ISearchRequestDto, SearchField, SearchOption, SearchPaginatedDetailDto, SearchResultDocumentResponse, TagSearchType } from 'generated/search-backend';
import useStore, { IStore } from 'app/store';
import SearchResultList from '../searchResultList/SearchResultList';
import { containsHashtag, escapeToSafeCharacters, getQueryParam, setQueryParam } from 'app/components/helpers';
import { Trans, useTranslation } from 'react-i18next';
import Pagination from 'app/components/pagination/Pagination';
import { StringConstants, RouteConstants, appInsightsService } from 'app/utils/constants';
import { IColumn, IColumnLookUpProps, IStateData } from 'app/models/common/post';
import { StateData } from 'app/modules/admin/manageRequests/ManageRequests';
import FilterPanel from '../../../admin/manageRequests/filterPanel/FilterPanel';
import { getOrderBy } from '../../../admin/manageRequests/filterPanel/sortFilter/SortFilter';
import { IOptionProps } from '../../../admin/manageRequests/models';
import { IUserProps } from 'app/models/common/response';
import { Loader } from 'app/components/loader/Loader';
import { isUser } from 'app/utils/authUtilities';
import { UserDto } from 'generated/user-backend';
import { saveAs } from 'file-saver';
import { getSearchFieldParams } from 'app/components/searchInDropdown/SearchInDropdown';

const SearchResults: React.FC = () => {

    const location = useLocation();
    const history = useHistory();

    const setUserSearchValue = useStore((state: IStore) => state.setUserSearchValue);
    const userSearchValue = useStore((state: IStore) => state.userSearchValue);
    const setSearchValueClear = useStore((state: IStore) => state.setSearchValueClear);
    const userDetails = useStore((state: IStore) => state.userDetails);
    const { t: translate } = useTranslation();
    const searchOrigin = useStore((state: IStore) => state.searchOrigin);
    const { mutate: searchData, data: searchResultData, isSuccess: isSuccessSearch, isLoading: isSearching } = useSearchResult();
    const { mutate: fetchRequestCount, data: requestCountData, isSuccess: isRequestCountSuccess } = useSearchResult();
    const { mutate: fetchArticleCount, data: articleCountData, isSuccess: isArticleCountSuccess } = useSearchResult();
    const {
        mutate: downloadSearchResultMutation,
        data: downloadSearchResultData,
        isLoading: isLoadingDownloadSearchResult,
        isSuccess: isSuccessDownloadSearchResult
    } = useDownloadSearchResults();
    const [listData, setListData] = useState<SearchResultDocumentResponse[]>([]);

    const [pageIndex, setPageIndex] = useState(Number(getQueryParam(history).page) || 1);

    const [articlesCount, setArticlesCount] = useState<number>(0);
    const [helpRequestCount, setHelpRequestCount] = useState<number>(0);

    const [searchPaginatedDetails, setSearchPaginatedDetails] = useState<SearchPaginatedDetailDto[]>([]);

    // Persist the search origin — initialized from location.state (back-nav) or store (first visit)
    const [persistedSearchOrigin] = useState<string>(() => {
        return (location?.state as IStateData)?.searchOrigin || searchOrigin || '';
    });

    // Search in fields — driven by global store so homepage/dashboard selections carry over
    const searchInFields = useStore((state: IStore) => state.searchInFields);
    const tagSearchType = useStore((state: IStore) => state.tagSearchType);
    const setTagSearchType = useStore((state: IStore) => state.setTagSearchType);
    // Ref to read tagSearchType inside effects without adding it as a dependency
    const tagSearchTypeRef = useRef(tagSearchType);
    tagSearchTypeRef.current = tagSearchType;

    // Local state for integrating existing Manage Requests FilterPanel (with sections hidden)
    const [requestTypeSelection, setRequestTypeSelection] = useState<IOptionProps[]>([]);
    const [statusSelection, setStatusSelection] = useState<IOptionProps[]>([]);
    const [agingSelection, setAgingSelection] = useState<IOptionProps[]>([]);
    const [productsSelection, setProductsSelection] = useState<IOptionProps[]>([]);
    const [countrySelection, setCountrySelection] = useState<IOptionProps[]>([]);
    const [assignedToSelection, setAssignedToSelection] = useState<IUserProps[]>([]);
    const [relationshipSelection, setRelationshipSelection] = useState<IOptionProps[]>([]);
    const [filterUserOptions, setFilterUserOptions] = useState<IUserProps[]>([]);
    const [DCFSelection, setDCFSelection] = useState<IOptionProps[]>([]);
    const [columnsLookUpData, setColumnsLookUp] = useState<IColumnLookUpProps[]>([]);
    const [list, setList] = useState<IColumn[]>([]);
    const [showMenu, setShowMenu] = useState(false);
    const handleShowMenu = () => setShowMenu((prev) => !prev);

    const checkAdminOrigin = (origin: string | undefined | null) => {
        if (!origin) return false;
        return (
            !isUser(userDetails?.userRoles) &&
            (origin.includes('manage-requests') ||
                (!isUser(userDetails?.userRoles) && origin === '/') ||
                origin.includes('report') ||
                origin.includes('request-detail') ||
                origin.includes('visual-flow'))
        );
    };

    const effectiveIsAdminSearch = checkAdminOrigin(searchOrigin) || checkAdminOrigin(persistedSearchOrigin);

    // Stable serialized filter key — mirrors buildFilterString logic so that
    // "no selection" and "ALL selected" produce the same key (both mean no filter).
    // This prevents re-fetches when filter dropdowns open and set default selections.
    const filterKey = useMemo(() => {
        const normalize = (arr: IOptionProps[], allKey: string | number) =>
            arr.length === 0 || arr.some(a => a.key === allKey) ? '' : arr.map(a => a.key).sort().join(',');

        return JSON.stringify({
            status: normalize(statusSelection, StringConstants.ALL),
            aging: normalize(agingSelection, StringConstants.ALL),
            products: normalize(productsSelection, 0),
            country: normalize(countrySelection, 0),
            relationship: normalize(relationshipSelection, StringConstants.ALL)
        });
    }, [statusSelection, agingSelection, productsSelection, countrySelection, relationshipSelection]);

    const [searchLink, setSearchLink] = useState(SearchOption.HelpRequest);
    const [sortOption, setSortOption] = useState<string>('Relevance');
    const displayPageSize = 10;
    const initialRender = useRef(true);

    const showDownloadBtn = () => {
        return effectiveIsAdminSearch;
    };

    const handleDownloadSearchResults = () => {
        let pagesizeValue = searchResultData?.searchResult?.totalCount;
        if (pagesizeValue) {
            if (pagesizeValue > 1000) pagesizeValue = 1000;
        } else {
            pagesizeValue = 0;
        }
        const isHashTag = containsHashtag(userSearchValue || '');
        const searchParams = getSearchFieldParams(searchInFields);
        const filterString = buildFilterString({
            searchLink: SearchOption.HelpRequest,
            statusSelection,
            agingSelection,
            productsSelection,
            countrySelection,
            relationshipSelection
        });
        const payload: any = {
            searchTerm: isHashTag ? userSearchValue?.replace('#', '') : escapeToSafeCharacters(userSearchValue),
            isAdminSearch: true,
            pageIndex: 1,
            pageSize: pagesizeValue,
            highlightPreTag: '<b class=highlight>',
            highlightPostTag: '</b>',
            highlightFields: searchParams.highlightFields,
            searchField: searchParams.searchField,
            customSearchFields: searchParams.customSearchFields,
            searchOption: SearchOption.HelpRequest,
            select: []
        };
        if (isHashTag) {
            payload.tagSearchType = 'HashTag';
        }
        if (filterString) payload.filter = filterString;
        downloadSearchResultMutation(payload as ISearchRequestDto);
    };

    // Main data fetch — depends on searchLink (active tab) for pagination/display
    useEffect(() => {
        setListData([]);
        const locationData: IStateData = location?.state;
        if (userSearchValue?.trim()) {
            const filterString = buildFilterString({
                searchLink,
                statusSelection,
                agingSelection,
                productsSelection,
                countrySelection,
                relationshipSelection
            });
            const searchParams = getSearchFieldParams(searchInFields);
            let payload: ISearchRequestDto = {
                searchTerm: escapeToSafeCharacters(userSearchValue),
                pageIndex: pageIndex,
                pageSize: displayPageSize,
                searchOption: searchLink,
                searchPaginatedDetails: locationData?.searchPaginatedDetails ?? searchPaginatedDetails,
                isAdminSearch: effectiveIsAdminSearch,
                orderBy: getOrderBy(sortOption),
                highlightPreTag: '<b class=highlight>',
                highlightPostTag: '</b>',
                searchField: searchParams.searchField as SearchField,
                customSearchFields: searchParams.customSearchFields,
                highlightFields: searchParams.highlightFields
            };
            if (containsHashtag(userSearchValue || '')) {
                payload.tagSearchType = TagSearchType.HashTag;
                payload.searchTerm = userSearchValue?.replace('#', '');
            } else if (tagSearchTypeRef.current) {
                payload.tagSearchType = tagSearchTypeRef.current as TagSearchType;
                setTagSearchType(null);
            }
            if (filterString) payload.filter = filterString;
            searchData(payload);
        }
    }, [userSearchValue, searchLink, pageIndex, searchInFields, filterKey, sortOption]);

    // Tab count fetches — independent of active tab, fire on search term / filter / search mode changes
    useEffect(() => {
        if (userSearchValue?.trim()) {
            const searchTerm = containsHashtag(userSearchValue || '')
                ? userSearchValue?.replace('#', '')
                : escapeToSafeCharacters(userSearchValue);
            const filterString = buildFilterString({
                searchLink: SearchOption.HelpRequest,
                statusSelection,
                agingSelection,
                productsSelection,
                countrySelection,
                relationshipSelection
            });
            const requestPayload: any = {
                searchTerm,
                pageIndex: 1,
                pageSize: 1,
                searchOption: SearchOption.HelpRequest,
                isAdminSearch: effectiveIsAdminSearch,
                highlightPreTag: '<b class=highlight>',
                highlightPostTag: '</b>'
            };
            const searchParams = getSearchFieldParams(searchInFields);
            requestPayload.searchField = searchParams.searchField;
            requestPayload.customSearchFields = searchParams.customSearchFields;
            requestPayload.highlightFields = searchParams.highlightFields;
            if (containsHashtag(userSearchValue || '')) {
                requestPayload.tagSearchType = 'HashTag';
            } else if (tagSearchTypeRef.current) {
                requestPayload.tagSearchType = tagSearchTypeRef.current;
            }
            if (filterString) requestPayload.filter = filterString;
            fetchRequestCount(requestPayload);

            // Article count
            const articlePayload: any = {
                searchTerm,
                pageIndex: 1,
                pageSize: 1,
                searchOption: SearchOption.Article,
                isAdminSearch: false,
                searchField: searchParams.searchField,
                customSearchFields: searchParams.customSearchFields,
                highlightFields: searchParams.highlightFields
            };
            if (containsHashtag(userSearchValue || '')) {
                articlePayload.tagSearchType = 'HashTag';
            } else if (tagSearchTypeRef.current) {
                articlePayload.tagSearchType = tagSearchTypeRef.current;
            }
            fetchArticleCount(articlePayload);
        }
    }, [userSearchValue, searchInFields, filterKey]);

    useEffect(() => {
        const locationData: IStateData = location?.state;
        if (locationData?.userSearchValue) {
            if (
                initialRender.current === false &&
                locationData?.userSearchValue?.toLowerCase()?.replaceAll('#', '') === userSearchValue?.toLowerCase()?.replaceAll('#', '') &&
                userSearchValue.trim()
            ) {
                initialRender.current = false;
                setQueryParam(history, RouteConstants.SEARCH_RESULTS, { page: 1 }, true, {
                    ...stateData,
                    userSearchValue: userSearchValue,
                    searchLink: SearchOption.HelpRequest
                });
            }
        } else {
            setQueryParam(history, RouteConstants.SEARCH_RESULTS, { page: 1 }, true, {
                ...stateData,
                userSearchValue: userSearchValue,
                searchLink: SearchOption.HelpRequest
            });
        }
    }, [userSearchValue]);

    useEffect(() => {
        if (isSuccessSearch) {
            if (searchResultData && searchResultData.searchResult.items) {
                setListData(searchResultData.searchResult.items);
            }

            if (searchResultData)
                setSearchPaginatedDetails(searchResultData.searchPaginatedDetails);
        }
    }, [isSuccessSearch]);

    // Dedicated count handlers — independent of tab switching
    useEffect(() => {
        if (isRequestCountSuccess && requestCountData?.searchPaginatedDetails) {
            setHelpRequestCount(
                requestCountData.searchPaginatedDetails.find(
                    (res) => res.searchOption === SearchOption.HelpRequest
                )?.totalCount || 0
            );
        }
    }, [isRequestCountSuccess]);

    useEffect(() => {
        if (isArticleCountSuccess && articleCountData?.searchPaginatedDetails) {
            setArticlesCount(
                articleCountData.searchPaginatedDetails.find(
                    (res) => res.searchOption === SearchOption.Article
                )?.totalCount || 0
            );
        }
    }, [isArticleCountSuccess]);

    useEffect(() => {
        if (isSuccessSearch) {
            appInsightsService.logEvent('Number of articles and requests', {
                articlesCount: articlesCount?.toString() || '0',
                helpRequestCount: helpRequestCount?.toString() || '0'
            });

            if (!helpRequestCount && !articlesCount) {
                appInsightsService.logEvent('No results found for search', {
                    searchTerm: userSearchValue || 'No search term provided'
                });
            }
        }
    }, [articlesCount, helpRequestCount]);

    useEffect(() => {
        if (!isLoadingDownloadSearchResult && isSuccessDownloadSearchResult) {
            let blobData = new Blob([downloadSearchResultData.data], { type: 'text/csv;charset=utf-8' });
            saveAs(blobData, `SearchResults_${new Date().getTime()}.csv`);
        }
    }, [isLoadingDownloadSearchResult, isSuccessDownloadSearchResult]);

    useEffect(() => {
        setSearchValueClear(false);
    }, []);

    const stateData : StateData = useMemo(() => {
        return {
            userSearchValue: userSearchValue,
            searchLink: searchLink,
            searchPaginatedDetails: searchPaginatedDetails,
            searchOrigin: persistedSearchOrigin
        };
    }, [userSearchValue, searchLink, searchPaginatedDetails, persistedSearchOrigin]);

    useEffect(() => {
        const locationData = location.state as IStateData;
        if (locationData) {
            if (locationData?.userSearchValue) setUserSearchValue(locationData.userSearchValue);
            setSearchLink(locationData?.searchLink ?? SearchOption.HelpRequest);
            if (locationData?.searchPaginatedDetails) setSearchPaginatedDetails(locationData.searchPaginatedDetails);
            setPageIndex(Number(getQueryParam(history).page ?? 1));
        }
    }, [history?.location?.search, location?.state]);

    return (
        <div className="search-results-container single-column">
            <div className="right-sidebar full-width">
                <div className="right-sidebar-container">
                    <div className="top-controls stacked">
                        <div className="manage-request-cont">
                            <FilterPanel
                                isListView={true}
                                handleToggleView={() => { }}
                                requestTypeSelection={requestTypeSelection}
                                showSort={true}
                                sortSelectedKey={sortOption}
                                onSortChange={(key) => {
                                    setSortOption(key);
                                    setPageIndex(1);
                                }}
                                setRequestTypeSelection={setRequestTypeSelection}
                                list={list}
                                showMenu={showMenu}
                                columnsLookUpData={columnsLookUpData}
                                setList={setList}
                                handleChecked={() => { }}
                                handleShowMenu={handleShowMenu}
                                setColumnsLookUp={setColumnsLookUp}
                                statusSelection={statusSelection}
                                setStatusSelection={setStatusSelection}
                                agingSelection={agingSelection}
                                setAgingSelection={setAgingSelection}
                                productsSelection={productsSelection}
                                countrySelection={countrySelection}
                                setProductsSelection={setProductsSelection}
                                setCountrySelection={setCountrySelection}
                                assignedToSelection={assignedToSelection}
                                setAssignedToSelection={setAssignedToSelection}
                                relationshipSelection={relationshipSelection}
                                setRelationshipSelection={setRelationshipSelection}
                                options={filterUserOptions}
                                setOptions={setFilterUserOptions}
                                userPreference={undefined}
                                dateRange={undefined}
                                activeRange={undefined}
                                DCFSelection={DCFSelection}
                                setDCFSelection={setDCFSelection}
                                setPreferenceDataFlag={() => { }}
                                stateData={stateData}
                                setListBuilderFilters={() => { }}
                                setPreviousLocation={() => { }}
                                hideRequestType
                                hideAssignedTo
                                hideViewToggle
                                hideCustomizeColumns
                                alignFiltersRight
                            />
                        </div>
                        <div className="search-controls-row">
                            <div className="left-section">
                            </div>
                            <div className="right-section">
                                {isLoadingDownloadSearchResult && (
                                    <Loader loadingText="Downloading..." size={SpinnerSize.medium} labelPosition="left" />
                                )}
                                {showDownloadBtn() && searchLink === SearchOption.HelpRequest && (
                                    <DefaultButton
                                        primary
                                        text={translate('Download Search Results')}
                                        onClick={handleDownloadSearchResults}
                                        data-testid="download-button"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="tab-buttons below-filters">
                            <div className="buttonsContainer" role="tablist" aria-label="Search Result Tabs" aria-live="polite">
                                <button
                                    tabIndex={0}
                                    className={`btn ${searchLink === SearchOption.HelpRequest ? 'active' : ''}`}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        if (userSearchValue.trim()) {
                                            setQueryParam(history, RouteConstants.SEARCH_RESULTS, { page: 1 }, false, {
                                                ...stateData,
                                                searchLink: SearchOption.HelpRequest
                                            });
                                        }
                                    }}
                                    data-testid="requests-button"
                                    role="tab"
                                    aria-roledescription={`${helpRequestCount} total requests returned`}
                                    aria-selected={searchLink === SearchOption.HelpRequest ? 'true' : 'false'}
                                    aria-controls="helprequest-search-list-container"
                                    id="helprequest-tab">
                                    <Trans>Requests</Trans> {helpRequestCount >= 0 && `(${helpRequestCount})`}
                                </button>
                                <button
                                    className={`btn ${searchLink === SearchOption.Article ? 'active' : ''}`}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        if (userSearchValue.trim()) {
                                            setQueryParam(history, RouteConstants.SEARCH_RESULTS, { page: 1 }, false, {
                                                ...stateData,
                                                searchLink: SearchOption.Article
                                            });
                                        }
                                    }}
                                    role="tab"
                                    aria-roledescription={`${articlesCount} total articles returned`}
                                    aria-selected={searchLink === SearchOption.Article ? 'true' : 'false'}
                                    aria-controls="article-search-list-container"
                                    id="article-tab">
                                    <Trans>Articles</Trans> {articlesCount >= 0 && `(${articlesCount})`}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        className="search-list-container"
                        id={`${searchLink.toLowerCase()}-search-list-container`}
                        role="tabpanel"
                        tabIndex={0}
                        aria-labelledby={`${searchLink.toLowerCase()}-tab`}>
                        {isSuccessSearch && !isSearching && listData.length >= 0 && (
                            <div className="scroll-panel " id="scroll-panel">
                                {isSuccessSearch && listData.length > 0 && (
                                    <>
                                        {listData.map((item: SearchResultDocumentResponse, index: number) => (
                                            <div key={index} className='article-container'>
                                                <SearchResultList listData={item} key={item.document.id} isAdminSearch={effectiveIsAdminSearch} />
                                            </div>
                                        ))}
                                    </>
                                )}

                                {isSuccessSearch && listData.length === 0 && (
                                    <p>
                                        <Trans>No data to display</Trans>
                                    </p>
                                )}
                            </div>
                        )}
                        {!isSuccessSearch && !isSearching && (
                            <p>
                                <Trans>No data to display</Trans>
                            </p>
                        )}
                        {isSearching && (
                            <div className="loading-container m-t-50 m-b-50 height-70">
                                <Spinner size={SpinnerSize.large} label="Loading..." />
                            </div>
                        )}
                    </div>

                    {listData && listData.length > 0 && (
                        <div className="pagination-container">
                            <Pagination
                                pageCount={searchResultData?.searchResult.totalPages}
                                setPageNumber={setPageIndex}
                                initialPage={pageIndex - 1}
                                totalCount={searchResultData?.searchResult.totalCount}
                                resultsPerPage={displayPageSize}
                                updateURL={true}
                                stateData={stateData}
                            />
                        </div>
                    )}

                    <div className="search-need-container">
                        <YouNeed />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchResults;
